import { io } from 'socket.io-client';
import {
  useRef,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
  PropsWithChildren,
} from 'react';

import { Config } from 'config';
import { useLocalStorageListener } from 'hooks';
import { getToken, decryptToken } from 'utils/storage';
import { useAppDispatch, useAppSelector } from 'store';
import { setUpdatedToken, selectTokenUpdated } from 'store/slices/auth';

import {
  Socket,
  SocketEvents,
  RefundPdfUrlEvent,
  SocketContextProps,
  CheckoutPdfUrlEvent,
} from './types';

const getInitialState = (): SocketContextProps => ({
  isConnected: () => false,
  subscribe: () => () => {},
});

export const SocketContext =
  createContext<SocketContextProps>(getInitialState());

export const useSocket = () => useContext(SocketContext);

export const SocketContextProvider = ({ children }: PropsWithChildren) => {
  const dispatch = useAppDispatch();
  const isTokenUpdated = useAppSelector(selectTokenUpdated);
  const [socket, setSocket] = useState<Socket | undefined>(undefined);
  const socketRef = useRef<Socket | undefined>(undefined);

  const { currentValue: token } = useLocalStorageListener('accessToken');

  useEffect(() => {
    if (!token || Config.isIsolatedRehearsal) {
      return;
    }

    const newSocket = io(Config.socketURL, {
      reconnectionAttempts: 5,
      reconnectionDelayMax: 5000,
      transports: ['websocket', 'polling'],
      auth: (cb) => {
        const currentToken = getToken('accessToken') ?? decryptToken(token);
        cb({ token: currentToken });
      },
    });

    setSocket(newSocket);
    socketRef.current = newSocket;

    newSocket.onAny((eventName) => {
      console.log('socket event', eventName);
    });

    newSocket.io.on('error', (error) => {
      console.warn('socket error', error);
    });

    newSocket.on('connect', () => {
      console.log('socket connected, ID:', newSocket.id);
    });

    newSocket.on('disconnect', () => {
      console.log('socket disconnected');
    });

    newSocket.io.on('reconnect', (attempt) => {
      console.log('socket reconnected on attempt', attempt);
    });

    newSocket.io.on('reconnect_attempt', (attempt) => {
      console.log('socket reconnect attempt', attempt);
    });

    newSocket.io.on('reconnect_error', (error) => {
      console.warn('socket reconnect error', error);
    });

    newSocket.io.on('reconnect_failed', () => {
      console.warn(
        'socket reconnect failed — giving up after reconnectionAttempts limit. Realtime updates disabled until next page reload.'
      );
    });

    return () => {
      newSocket.disconnect();
    };
  }, [token]);

  useEffect(() => {
    if (isTokenUpdated && socket) {
      console.log('Token updated, forcing socket reconnect');
      socket.disconnect().connect();
      dispatch(setUpdatedToken(false));
    }
  }, [isTokenUpdated, socket, dispatch]);

  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        setSocket(undefined);
        socketRef.current = undefined;
      }
    };
  }, []);

  /**
   * Subscribes to a specific socket event with a callback, and cleans up the listener
   * when the subscription is no longer needed by calling the returned cleanup function.
   *
   * @param event - The name of the socket event to subscribe to.
   * @param callback - The callback function to be executed when the event is triggered.
   * @returns A cleanup function that removes the listener for the specified event.
   */
  const subscribe = useCallback(
    <K extends keyof SocketEvents>(event: K, callback: SocketEvents[K]) => {
      const eventString = String(event);
      const currentSocket = socketRef.current;

      currentSocket?.on(eventString, callback);

      return () => {
        currentSocket?.off(eventString, callback);
      };
    },
    []
  );

  return (
    <SocketContext.Provider
      value={{
        socket,
        subscribe,
        isConnected: () => socket?.connected || false,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export type { RefundPdfUrlEvent, CheckoutPdfUrlEvent };
